"use client";

import { ErrorBoundary, ErrorFallback } from "@atlas/composite";
import { Card, CardContent, CardTitle, Field, PromptInput } from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import type { IHubComment } from "@models";
import { CommentRow } from "./CommentRow";

export interface ICommentsComposerProps {
  draft: string;
  onDraftChange: (value: string) => void;
  onSubmit: (text: string) => void;
  isPosting: boolean;
  promptPlaceholder?: string;
}

interface ICommentsProps {
  catalogId: number;
  comments: IHubComment[];
  loading: boolean;
  error: boolean;
  composer?: ICommentsComposerProps | null;
}

export function Comments({
  catalogId,
  comments,
  loading,
  error,
  composer,
}: ICommentsProps) {
  const { t } = useTranslation();
  const defaultPlaceholder = t("hub.catalog_details.comment_body_placeholder", {
    defaultValue: "Write your comment…",
  });

  const list = (
    <div className="m-0 flex flex-col gap-0 p-0">
      {comments.map((c) => {
        const displayName =
          c.commentBy ??
          t("hub.sidebar.user", {
            defaultValue: "User",
          });
        return (
          <CommentRow
            key={
              c.commentId ??
              `${c.catalogId}-${c.commentBy ?? ""}-${c.content.slice(0, 24)}`
            }
            displayName={displayName}
            timestampIso={c.commentDatetime}
            body={c.content}
          />
        );
      })}
    </div>
  );

  return (
    <>
      {error && (
        <ErrorFallback
          title={t("hub.catalog_details.comments_load_error_title", {
            defaultValue: "Could not load comments",
          })}
          message={t("hub.catalog_details.comments_load_error", {
            defaultValue: "Comments could not be loaded.",
          })}
          showIcon={false}
          className="min-h-0 items-stretch justify-start p-0"
        />
      )}
      <ErrorBoundary
        title={t("hub.catalog_details.comments_load_error_title", {
          defaultValue: "Could not load comments",
        })}>
        {composer != null && catalogId > 0 && (
          <Field className="mt-3 gap-3">
            <CardTitle>
              {t("hub.catalog_details.comment_add_title", {
                defaultValue: "Add a comment",
              })}
            </CardTitle>
            <PromptInput
              value={composer.draft}
              onChange={composer.onDraftChange}
              onSubmit={composer.onSubmit}
              disabled={composer.isPosting}
              fullWidth
              maxRows={6}
              placeholder={composer.promptPlaceholder ?? defaultPlaceholder}
            />
          </Field>
        )}
        <Card
          variant="content"
          className="border-0 shadow-none"
          isLoading={loading}>
          <CardContent>{list}</CardContent>
        </Card>
      </ErrorBoundary>
    </>
  );
}
